import { useCallback, useEffect, useRef, useState } from "react"
import { Onfido as OnfidoSDK, Handle } from "onfido-sdk-ui"
import { Button } from "@brainfinance/icash-component-library"

import faceIdIcon from "../../../images/identity-verification/iconoir_face-id.svg"

type OnfidoCompleteData = {
  [key: string]: unknown
}

type OnfidoError = {
  type: string
  message: string
}

type OnfidoProps = {
  token: string
  workflowRunId: string
  containerId?: string
  onComplete: (data: OnfidoCompleteData) => void
  onError: (error: OnfidoError) => void
  onUserExit: () => void
}

export function Onfido({
  token,
  workflowRunId,
  containerId = "onfido-mount",
  onComplete,
  onError,
  onUserExit,
}: OnfidoProps) {
  const onfidoRef = useRef<Handle | null>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (!token || !workflowRunId) {
      return
    }

    try {
      onfidoRef.current = OnfidoSDK.init({
        token,
        containerId,
        workflowRunId,
        onComplete: (data: Record<string, unknown>) => {
          onComplete(data as OnfidoCompleteData)
        },
        onError: (sdkError: { type?: string; message?: string }) => {
          console.error("Onfido error:", sdkError)
          const err: OnfidoError = {
            type: sdkError.type || "unknown",
            message: sdkError.message || "An error occurred during identity verification",
          }
          onError(err)
        },
      })

      setIsReady(true)
    } catch (err) {
      console.error("Failed to initialize Onfido SDK:", err)
      const initError: OnfidoError = {
        type: "INIT_ERROR",
        message: err instanceof Error ? err.message : "Failed to initialize Onfido SDK",
      }
      onError(initError)
    }

    return () => {
      if (onfidoRef.current) {
        onfidoRef.current.tearDown()
        onfidoRef.current = null
      }
    }
  }, [token, workflowRunId, containerId, onError])

  return (
    <div
      className="max-w-[622px]"
    >
      {/* Loading overlay */}
      {!isReady && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
          <img src={faceIdIcon} alt="Identity verification" className="h-[82px] w-[82px] mb-[32px]" />
          <h2 className="caption-light-no-color !text-[#13141A] !font-[600] !mb-[16px] !text-center">
            Preparing verification...
          </h2>
          <p className="caption-medium !text-center">
            Please wait while we set up the identity verification process.
          </p>
        </div>
      )}
      {/* Onfido SDK container - always rendered */}
       <div
        id={containerId}
        className="h-[800px]"
        style={{ visibility: isReady ? "visible" : "hidden" }}
      />
    </div>
  )
}
